import React, { useMemo } from 'react';
import { groupAssignmentsForSignoff, type Assignment } from '../utils/signoffGrouping';

export type SnapRow = {
  lockedAssignmentId: string | number;
  storeId: string | number;
  storeName: string;
  operatorId: string | number | null;
  operatorName: string;
  formatId: string | number | null;
  formatName: string | null;
  campaignId: string | number | null;
  campaignName: string | null;
  expectedQuantity: number;
  taskId: string | number | null;
  taskName: string | null;
  packId?: string | number | null;
  isPackAssignment?: boolean;
};

export function SignOffTable({ rows }: { rows: SnapRow[] }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-600">No assignments found for this cycle.</div>;
  }
  
  const safeRows: SnapRow[] = useMemo(() => (rows || []).map((r: any) => ({
    lockedAssignmentId: r.lockedAssignmentId ?? r.locked_assignment_id ?? r.id ?? String(Math.random()),
    storeId: r.storeId ?? r.store_id,
    storeName: r.storeName ?? r.store_name ?? 'Unknown Store',
    operatorId: r.operatorId ?? r.operator_id ?? null,
    operatorName: r.operatorName ?? r.operator_name ?? 'Unknown Operator',
    formatId: r.formatId ?? r.format_id ?? null,
    formatName: r.formatName ?? r.format_name ?? null,
    campaignId: r.campaignId ?? r.campaign_id ?? null,
    campaignName: r.campaignName ?? r.campaign_name ?? 'Unassigned',
    expectedQuantity: Number(r.expectedQuantity ?? r.expected_quantity ?? 0),
    taskId: r.taskId ?? r.task_id ?? null,
    taskName: r.taskName ?? r.task_name ?? 'Unknown Task',
    packId: r.packId ?? r.pack_id ?? null,
    isPackAssignment: r.isPackAssignment ?? r.is_pack_assignment ?? false,
  })), [rows]);
  
  // Convert to Assignment interface for grouping
  const assignments: Assignment[] = useMemo(() => safeRows.map(row => ({
    lockedAssignmentId: row.lockedAssignmentId,
    storeId: row.storeId,
    storeName: row.storeName,
    operatorId: row.operatorId,
    operatorName: row.operatorName,
    formatId: row.formatId,
    formatName: row.formatName,
    campaignId: row.campaignId,
    campaignName: row.campaignName,
    expectedQuantity: row.expectedQuantity,
    taskId: row.taskId,
    taskName: row.taskName,
    packId: row.packId,
    isPackAssignment: row.isPackAssignment,
  })), [safeRows]);
  
  // Always group assignments for better readability
  const groupedData = useMemo(() => {
    return groupAssignmentsForSignoff(assignments);
  }, [assignments]);
  
  // Always render grouped table
  if (groupedData) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 p-2 text-left">Campaign</th>
              <th className="border border-gray-300 p-2 text-left">Task</th>
              <th className="border border-gray-300 p-2 text-left">Media Format</th>
              <th className="border border-gray-300 p-2 text-left">Expected</th>
              <th className="border border-gray-300 p-2 text-left">Delivered</th>
              <th className="border border-gray-300 p-2 text-left">Comments</th>
            </tr>
          </thead>
          <tbody>
            {/* Pack Groups */}
            {groupedData.groups.map((group) => (
              <React.Fragment key={group.groupKey}>
                {/* Group Items - each media format gets its own row */}
                {group.items.map((item, index) => (
                  <tr key={item.assignmentId} className="bg-gray-50">
                    {/* Campaign and Task columns - only show on first row of group */}
                    {index === 0 ? (
                      <>
                        <td 
                          className="border border-gray-300 p-2 font-semibold bg-blue-50 border-l-4 border-l-blue-400" 
                          rowSpan={group.items.length}
                          role="rowheader"
                          aria-label={`Campaign ${group.campaignName}`}
                        >
                          {group.campaignName}
                        </td>
                        <td 
                          className="border border-gray-300 p-2 font-semibold bg-blue-50" 
                          rowSpan={group.items.length}
                          role="rowheader"
                          aria-label={`Task ${group.taskLabel}`}
                        >
                          {group.taskLabel}
                        </td>
                      </>
                    ) : null}
                    <td className="border border-gray-300 p-2">{item.mediaFormatName}</td>
                    <td className="border border-gray-300 p-2 text-center">{item.expectedQty > 0 ? item.expectedQty : '—'}</td>
                    <td className="border border-gray-300 p-2">—</td>
                    <td className="border border-gray-300 p-2"></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            
            {/* Single Assignments */}
            {groupedData.singles.map((single) => (
              <tr key={single.assignmentId}>
                <td className="border border-gray-300 p-2">{single.campaignName}</td>
                <td className="border border-gray-300 p-2">{single.taskLabel}</td>
                <td className="border border-gray-300 p-2">{single.mediaFormatName}</td>
                <td className="border border-gray-300 p-2 text-center">{single.expectedQty > 0 ? single.expectedQty : '—'}</td>
                <td className="border border-gray-300 p-2">—</td>
                <td className="border border-gray-300 p-2"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback: if grouping fails for some reason, show a simple message
  return <div className="text-sm text-gray-600">Unable to display assignments data.</div>;
}

export default SignOffTable;


